import { useState, useMemo, useEffect } from "react";
import { useLocation } from "wouter";
import { useUser } from "@/lib/user";
import { api, HttpError } from "@/lib/api";
import { QUEST_META } from "@/lib/quests";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  CreditCard,
  IndianRupee,
  DollarSign,
  CheckCircle2,
  BookOpen,
  Users,
  Sparkles,
  AlertTriangle,
  Check,
  UserPlus,
} from "lucide-react";

// XP lookup by quest id (uses the same source of truth the dashboard uses).
const XP_BY_QID = QUEST_META.reduce((acc, q) => ({ ...acc, [q.id]: q.xp }), {});

// Minimal email sanity check — server is the source of truth, this is only for
// gating the "pay" buttons when the user hasn't logged in yet.
function isValidEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Razorpay script failed to load"));
    document.head.appendChild(s);
  });
}

// Small helper: hide an img when its src 404s. Used for the book cover slot
// while the user is still uploading /founder-mode-cover.jpg.
function hideOnError(e) {
  e.currentTarget.style.display = "none";
}

export default function PaywallPage() {
  const [, navigate] = useLocation();
  const { user, setUser } = useUser();
  const [busy, setBusy] = useState(null); // "razorpay" | "stripe" | null
  const [razorpayMocked, setRazorpayMocked] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);

  // Inline signup — only used when the user hits /paywall anonymously.
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");

  useEffect(() => {
    if (!user) {
      // Anonymous is now allowed — inline signup renders below.
      setProgress(null);
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const list = await api.getProgressAll(user.id, { signal: controller.signal });
        setProgress(list);
      } catch (_) {
        // Non-blocking; the banner just falls back to the empty-state copy.
      }
    })();
    return () => controller.abort();
  }, [user]);

  const { completedCount, xpEarned } = useMemo(() => {
    if (!progress) return { completedCount: 0, xpEarned: 0 };
    let c = 0;
    let xp = 0;
    for (const p of progress) {
      if (p.status === "completed") {
        c += 1;
        xp += XP_BY_QID[p.quest_id] || 0;
      }
    }
    return { completedCount: c, xpEarned: xp };
  }, [progress]);

  const isIndia = useMemo(() => {
    const lang = (navigator.language || "").toLowerCase();
    return lang.startsWith("en-in") || lang.startsWith("hi");
  }, []);

  const isAnon = !user;
  const signupReady =
    signupName.trim().length > 0 && isValidEmail(signupEmail);
  const canPay = !isAnon || signupReady;

  // Silent create-user if anonymous; returns the resolved user (either
  // pre-existing context user or the just-created one). Sets it into context.
  async function ensureUser() {
    if (user) return user;
    const u = await api.login(signupEmail.trim(), signupName.trim());
    setUser(u);
    return u;
  }

  async function handleRazorpay() {
    setError("");
    setBusy("razorpay");
    try {
      const u = await ensureUser();
      const order = await api.razorpayCreate(u.id);
      if (order.mocked) {
        setRazorpayMocked(true);
        setBusy(null);
        return;
      }
      await loadRazorpayScript();
      const options = {
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "The Founder Mode Quest",
        description: "Founder Pass · Lifetime access",
        prefill: { name: u.name, email: u.email },
        theme: { color: "#f59e0b" },
        handler: async (resp) => {
          try {
            const v = await api.verifyPayment({
              user_id: u.id,
              provider: "razorpay",
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });
            if (v.success) {
              navigate(
                "/payment-success?provider=razorpay&payment_id=" +
                  encodeURIComponent(resp.razorpay_payment_id)
              );
            } else {
              setError("Razorpay verification failed.");
            }
          } catch (e) {
            setError("Verification error: " + (e?.body?.detail || e?.message || "unknown"));
          } finally {
            setBusy(null);
          }
        },
        modal: {
          ondismiss: () => setBusy(null),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        setError("Payment failed: " + (resp?.error?.description || "unknown"));
        setBusy(null);
      });
      rzp.open();
    } catch (e) {
      if (e instanceof HttpError)
        setError("Razorpay setup error: " + (e?.body?.detail?.[0]?.msg || e?.body?.detail || e.message));
      else setError("Razorpay setup error: " + e.message);
      setBusy(null);
    }
  }

  async function handleStripe() {
    setError("");
    setBusy("stripe");
    try {
      const u = await ensureUser();
      const out = await api.stripeCreate(u.id, window.location.origin);
      window.location.href = out.checkout_url;
    } catch (e) {
      setError(
        "Stripe setup error: " +
          (e?.body?.detail?.[0]?.msg || e?.body?.detail || e?.message || "unknown")
      );
      setBusy(null);
    }
  }

  const progressText =
    completedCount > 0
      ? `You've completed ${completedCount} quest${completedCount === 1 ? "" : "s"}. ${xpEarned} XP earned.`
      : "Quest 0 & 1 unlocked. Ready for the next 11?";

  return (
    <main data-testid="paywall-page" className="min-h-screen px-6 sm:px-10 lg:px-20 py-12">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate(isAnon ? "/" : "/dashboard")}
          data-testid="paywall-back-btn"
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 mb-8"
        >
          <ArrowLeft className="h-4 w-4" /> {isAnon ? "Back home" : "Back to dashboard"}
        </button>

        {/* Dynamic progress banner */}
        <div
          data-testid="paywall-progress-banner"
          className="inline-flex items-center gap-2 rounded-full border border-amber-500/25 bg-amber-500/[0.05] px-4 py-1.5 mb-8"
        >
          <Sparkles className="h-3.5 w-3.5 text-amber-300" />
          <span className="font-mono text-[12px] tracking-wide text-amber-200">
            {progressText}
          </span>
        </div>

        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300">
          the founder pass
        </p>
        <h1
          data-testid="paywall-title"
          className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-zinc-50 break-words"
        >
          Unlock your full path to <span className="text-amber-400">$10M</span>
          <span className="text-amber-400">.</span>
        </h1>

        {/* Value stack + book cover */}
        <div className="mt-12 grid lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-14 items-start">
          <ul data-testid="paywall-value-stack" className="space-y-6 min-w-0">
            <li className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-amber-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-lg font-semibold text-zinc-100">
                  All 13 AI-powered quests + your personalized roadmap
                </div>
                <p className="mt-1 text-sm text-zinc-500 leading-relaxed">
                  Quests 2 through 12 — problem-solution fit, JTBD, ICP, pricing, business
                  models, $10M sprint, scale, virality.
                </p>
              </div>
            </li>

            <li className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-amber-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-lg font-semibold text-zinc-100 flex flex-wrap items-center gap-2">
                  <span>The Founder Mode — full book (PDF), 35 chapters</span>
                  <span className="inline-flex items-center gap-2 text-sm">
                    <span className="line-through text-zinc-600 font-mono">₹999</span>
                    <span className="text-amber-300 font-medium">included free</span>
                  </span>
                </div>
                <p className="mt-1 text-sm text-zinc-500 leading-relaxed">
                  Instant download after payment. Yours forever.
                </p>
              </div>
            </li>

            <li className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-amber-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-lg font-semibold text-zinc-100">
                  Lifetime access to the 1000 Founders Community
                </div>
                <p className="mt-1 text-sm text-zinc-500 leading-relaxed">
                  599+ founders. Weekly voice chats. Real referrals and intros.
                </p>
                <p className="mt-1 text-[11px] font-mono uppercase tracking-wider text-zinc-600">
                  private whatsapp group
                </p>
              </div>
            </li>
          </ul>

          {/* Book cover — right column on desktop, top on mobile */}
          <aside
            data-testid="paywall-book-cover-slot"
            className="hidden sm:flex flex-col items-center gap-3 lg:pt-4"
          >
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl bg-amber-500/10 blur-2xl" aria-hidden="true" />
              <img
                data-testid="paywall-book-cover"
                src="/founder-mode-cover.jpg"
                alt="The Founder Mode — book cover"
                loading="lazy"
                onError={hideOnError}
                className="relative w-48 lg:w-56 rounded-lg border border-amber-500/30 shadow-2xl shadow-amber-900/40 rotate-[-3deg] hover:rotate-0 transition-transform"
              />
            </div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 text-center">
              the book · 35 chapters · included
            </p>
          </aside>
        </div>

        {/* Price block */}
        <div data-testid="paywall-price-block" className="mt-16 text-center">
          <div className="font-mono text-5xl sm:text-6xl text-amber-400 leading-none">
            ₹4,997
          </div>
          <p className="mt-3 text-zinc-300 text-base">one payment, yours forever</p>
          <p className="mt-1 text-zinc-500 text-sm">$197 for international customers via Stripe below</p>
        </div>

        {/* INLINE SIGNUP — only for anonymous visitors. Sits directly above the
            payment cards so the user never leaves this page to create an account. */}
        {isAnon && (
          <section
            data-testid="paywall-inline-signup"
            className="mt-10 max-w-3xl mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/[0.04] p-6 sm:p-7"
          >
            <div className="flex items-center gap-2 text-amber-300">
              <UserPlus className="h-4 w-4" />
              <span className="font-mono text-[11px] uppercase tracking-wider">
                one quick step before payment
              </span>
            </div>
            <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
              Enter your name and email — we&apos;ll spin up your account instantly and
              hand you straight over to secure payment.
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
                  Name
                </label>
                <Input
                  data-testid="paywall-signup-name"
                  value={signupName}
                  onChange={(e) => setSignupName(e.target.value)}
                  placeholder="Praveen"
                  className="bg-zinc-900/60 border-amber-500/20 text-zinc-100 placeholder:text-zinc-600 h-11"
                  disabled={busy !== null}
                  autoComplete="name"
                />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
                  Email
                </label>
                <Input
                  data-testid="paywall-signup-email"
                  type="email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  placeholder="you@founder.in"
                  className="bg-zinc-900/60 border-amber-500/20 text-zinc-100 placeholder:text-zinc-600 h-11"
                  disabled={busy !== null}
                  autoComplete="email"
                />
              </div>
            </div>
            {!signupReady && (
              <p
                data-testid="paywall-signup-helper"
                className="mt-3 text-[11px] text-zinc-500 inline-flex items-center gap-1.5"
              >
                <Check className="h-3 w-3 text-emerald-400" />
                Fill both fields to enable the payment buttons.
              </p>
            )}
          </section>
        )}

        {/* Payment cards */}
        <section className="mt-8 grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {razorpayMocked && (
            <div
              data-testid="razorpay-mocked-banner"
              className="sm:col-span-2 rounded-xl border border-amber-500/40 bg-amber-500/[0.08] p-4 flex gap-3 items-start"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-300 shrink-0" />
              <div className="text-sm text-amber-100">
                <strong className="text-amber-300">Razorpay is in DEMO mode.</strong> Use Stripe
                to complete a test payment.
              </div>
            </div>
          )}

          {/* India · Razorpay */}
          <article
            data-testid="razorpay-card"
            className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-6"
          >
            <div className="flex items-center gap-2 text-amber-300">
              <IndianRupee className="h-4 w-4" />
              <span className="font-mono text-[11px] uppercase tracking-wider">
                india · razorpay
              </span>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-mono text-3xl md:text-4xl text-zinc-50">₹4,997</span>
              <span className="text-xs text-zinc-500">one-time</span>
            </div>
            <Button
              data-testid="razorpay-pay-btn"
              onClick={handleRazorpay}
              disabled={busy !== null || razorpayMocked || !canPay}
              className="mt-5 w-full h-11 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
            >
              <CreditCard className="h-4 w-4" />
              {busy === "razorpay"
                ? "Opening Razorpay…"
                : razorpayMocked
                  ? "Razorpay disabled (demo mode)"
                  : "Pay with Razorpay"}
            </Button>
          </article>

          {/* International · Stripe */}
          <article
            data-testid="stripe-card"
            className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-6"
          >
            <div className="flex items-center gap-2 text-amber-300">
              <DollarSign className="h-4 w-4" />
              <span className="font-mono text-[11px] uppercase tracking-wider">
                international · stripe
              </span>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-mono text-3xl md:text-4xl text-zinc-50">$197</span>
              <span className="text-xs text-zinc-500">one-time</span>
            </div>
            <Button
              data-testid="stripe-pay-btn"
              onClick={handleStripe}
              disabled={busy !== null || !canPay}
              className="mt-5 w-full h-11 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
            >
              <CreditCard className="h-4 w-4" />
              {busy === "stripe" ? "Redirecting to Stripe…" : "Pay with Stripe"}
            </Button>
          </article>

          {error && (
            <p data-testid="paywall-error" className="sm:col-span-2 text-xs text-red-400">
              {error}
            </p>
          )}
        </section>

        {/* Trust line */}
        <p
          data-testid="paywall-trust-line"
          className="mt-8 text-center text-sm text-zinc-500 max-w-2xl mx-auto leading-relaxed"
        >
          <Check className="inline h-3.5 w-3.5 text-emerald-400 -mt-0.5 mr-1" />
          Secure payment. Instant unlock. If you can&apos;t complete your first quest, we&apos;ve failed
          you — email{" "}
          <a
            href="mailto:praveen@thefoundermode.in"
            className="text-amber-300 hover:text-amber-200"
          >
            praveen@thefoundermode.in
          </a>{" "}
          and we&apos;ll help.
        </p>

        {/* Tiny previews of what's coming — reinforce the two "bonus" pillars */}
        <div className="mt-12 grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto text-[11px] font-mono uppercase tracking-wider text-zinc-500">
          <div className="rounded-lg border border-zinc-800/80 bg-zinc-950/40 px-4 py-3 flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5 text-amber-400" /> book pdf delivered on next screen
          </div>
          <div className="rounded-lg border border-zinc-800/80 bg-zinc-950/40 px-4 py-3 flex items-center gap-2">
            <Users className="h-3.5 w-3.5 text-amber-400" /> community invite on next screen
          </div>
        </div>

        {/* Region hint (kept subtle; the geo "recommended" badges are gone). */}
        <p className="mt-8 text-center text-[11px] font-mono uppercase tracking-wider text-zinc-600">
          {isIndia ? "detected · india · razorpay works out of the box" : "outside india · stripe works out of the box"}
        </p>
      </div>
    </main>
  );
}
